// src/services/subscriptionPlans.js
//
// Membership plan data for the yearly-plan picker. Prices come from
// /api/subscription-plans (live Stripe prices), cached in localStorage so a
// returning visitor paints the picker immediately (stale-while-revalidate, the
// same approach as the siteConfig cache).
//
// SAFETY CONTRACT: `buildAnnualOffer` returns null unless the data is complete
// and sane (monthly = month, yearly = year, same currency, yearly actually
// cheaper). Every caller treats null as "show today's monthly-only UI". So a
// Stripe blip, a missing env var, or a misconfigured price can only ever HIDE
// the yearly option — never break a subscribe button.
// See docs/decisions/adr-annual-plan.md.

const LS_KEY = 'mom_subscription_plans_v1';
const PLAN_CHOICE_KEY = 'mom_selected_plan';
const PLAN_CHOICE_TTL_MS = 5 * 60 * 1000;

let inflight = null;

function isValidPlans(p) {
    return !!(p && p.monthly && typeof p.monthly.amount === 'number' && p.monthly.interval === 'month');
}

export function getCachedPlans() {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return isValidPlans(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

// Resolves to the plans object, or null on any failure (never throws).
export function fetchSubscriptionPlans() {
    if (inflight) return inflight;
    inflight = fetch('/api/subscription-plans')
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
            if (!isValidPlans(data)) { inflight = null; return null; }
            try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch { /* storage unavailable */ }
            return data;
        })
        .catch(() => { inflight = null; return null; });
    return inflight;
}

export function formatMoney(cents, currency) {
    if (typeof cents !== 'number' || !isFinite(cents)) return '';
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: (currency || 'usd').toUpperCase(),
            minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
            maximumFractionDigits: 2,
        }).format(cents / 100);
    } catch {
        return '';
    }
}

// { monthly, annual, savings, monthlyPrice:'$19/mo', annualPrice:'$179/yr',
//   annualPlain:'$179', savingsPlain:'$49' } — or null.
export function buildAnnualOffer(plans) {
    if (!isValidPlans(plans)) return null;
    const { monthly, annual } = plans;
    if (!annual || annual.interval !== 'year' || typeof annual.amount !== 'number') return null;
    if ((monthly.intervalCount || 1) !== 1 || (annual.intervalCount || 1) !== 1) return null;
    if (annual.currency !== monthly.currency) return null;
    const savings = monthly.amount * 12 - annual.amount;
    if (!(savings > 0)) return null;
    const monthlyPlain = formatMoney(monthly.amount, monthly.currency);
    const annualPlain = formatMoney(annual.amount, annual.currency);
    const savingsPlain = formatMoney(savings, monthly.currency);
    if (!monthlyPlain || !annualPlain || !savingsPlain) return null;
    return {
        monthly,
        annual,
        savings,
        monthlyPlain,
        annualPlain,
        savingsPlain,
        monthlyPrice: `${monthlyPlain}/mo`,
        annualPrice: `${annualPlain}/yr`,
    };
}

// Admin-editable copy with fallbacks. `{amount}` / `{price}` are substituted.
export function resolvePlanCopy(c = {}) {
    const pick = (key, fallback) => (typeof c[key] === 'string' && c[key].trim() ? c[key] : fallback);
    return {
        yearlyLabel: pick('planYearlyLabel', 'Yearly'),
        monthlyLabel: pick('planMonthlyLabel', 'Monthly'),
        savingsTemplate: pick('planSavingsTemplate', 'Save {amount}'),
        yearlyNote: pick('planYearlyNote', 'Billed {price} once a year'),
        monthlyNote: pick('planMonthlyNote', 'Billed monthly'),
        pickerHeading: pick('planPickerHeading', 'Choose your membership'),
        continueLabel: pick('planPickerContinueLabel', 'Continue'),
        switchLabel: pick('switchYearlyLabel', 'Switch to yearly'),
    };
}

export function fillTemplate(template, tokens) {
    return String(template || '').replace(/\{(\w+)\}/g, (m, k) => (tokens[k] != null ? tokens[k] : m));
}

// ── Plan choice across a Clerk sign-up/sign-in (the modal can remount the
// page, so component state alone is lost — see feedback_clerk_remount_pitfall).
export function rememberPlanChoice(plan) {
    try {
        if (plan === 'annual' || plan === 'monthly') {
            sessionStorage.setItem(PLAN_CHOICE_KEY, JSON.stringify({ plan, at: Date.now() }));
        }
    } catch { /* sessionStorage unavailable */ }
}

// Returns 'annual' | 'monthly' | null and clears the stored value.
export function consumePlanChoice() {
    try {
        const raw = sessionStorage.getItem(PLAN_CHOICE_KEY);
        sessionStorage.removeItem(PLAN_CHOICE_KEY);
        if (!raw) return null;
        const { plan, at } = JSON.parse(raw);
        if (!at || Date.now() - at > PLAN_CHOICE_TTL_MS) return null;
        return plan === 'annual' || plan === 'monthly' ? plan : null;
    } catch {
        return null;
    }
}
